/**
 * Human-readable descriptions of tool calls, for the "thought process" trace.
 *
 * Exhaustive over KnownToolName by construction: the `never` check in the
 * default branch makes it a compile error to add a tool to
 * @amzn/soda-brain-protocol's KNOWN_TOOL_NAMES without adding a case here —
 * catching drift at build time instead of silently falling back to a generic
 * label at runtime.
 */
import type { ToolCallMetadata } from '@amzn/soda-brain-protocol';

/** One-line description of a tool call, suitable for the thought-process trace. */
export function describeToolCall(call: ToolCallMetadata): string {
  switch (call.tool) {
    case 'query_andes':
      return `🗄️ Running SQL:\n\`\`\`sql\n${call.input.sql}\n\`\`\``;
    case 'search_knowledge_base':
      return `🔍 Searching knowledge base: "${call.input.query}"`;
    case 'read_file':
      return `📄 Reading file: ${call.input.s3Key}`;
    case 'list_files':
      return `📁 Listing files: ${call.input.prefix}`;
    case 'ask_clarification':
      return `❓ Asking: ${call.input.question}`;
    case 'sports_data_platform_lookup':
      return `🏈 Looking up ${call.input.entityType}: ${call.input.query}`;
    case 'list_andes_tables':
      return call.input.tableName
        ? `📋 Listing Andes tables matching: ${call.input.tableName}`
        : '📋 Listing Andes tables';
    case 'read_table_sample':
      return `🔬 Sampling table: ${call.input.tableName}`;
    case 'read_event_scout':
      return call.input.search
        ? `📅 Reading event scout (${call.input.date}): "${call.input.search}"`
        : `📅 Reading event scout (${call.input.date})`;
    case 'read_launch_scout':
      return call.input.launchId
        ? `🚀 Reading launch scout: ${call.input.launchId}`
        : `🚀 Reading launch scout: "${call.input.name}"`;
    case 'read_external_events_raw':
      return `📅 Reading external events: ${call.input.date}`;
    case 'analyze_data':
      return `📊 Analyzing: ${call.input.question}`;
    case 'research_conversation_history':
      return `🧭 Researching conversation history: ${call.input.question}`;
    // Sub-agent-only tools (never called directly by the main agent — see
    // ResearchConversationHistoryInput's docstring): the history-research
    // sub-agent uses these internally while resolving research_conversation_history.
    // They're part of ToolCallMetadata's exhaustive union, so kept generic here.
    case 'search_conversation_memory':
      return `🧭 Searching conversation memory: "${call.input.query}"`;
    case 'get_turn_memory':
      return `🧭 Reading turn memory: ${call.input.turnId}`;
    case 'get_execution_events':
      return `🧭 Reading execution events: ${call.input.turnId}`;
    case 'get_tool_result':
      return `🧭 Reading tool result: ${call.input.toolUseId}`;
    default: {
      // Exhaustiveness guard — a new KnownToolName was added without a case here.
      const exhaustive: never = call;
      return `🔧 ${(exhaustive as ToolCallMetadata).tool}`;
    }
  }
}
